<template>
  <div v-if="isAdminUser" class="floating-message-container">
    <!-- 消息窗口 -->
    <div class="message-window" :class="{ show: isWindowVisible }">
      <!-- 可拉出的角标 -->
      <div class="pull-corner" @click="toggleMessageWindow">
        <div class="corner-line"></div>
        <div class="corner-line"></div>
        <div class="corner-line"></div>
      </div>

      <div class="message-header">
        <h3>消息记录</h3>
        <el-button type="text" @click="toggleMessageWindow">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>

      <div ref="messageListRef" class="message-list" :class="{ 'auto-scroll': shouldAutoScroll }">
        <div v-for="message in messages" :key="message.id" class="message-item">
          <div class="message-header-row">
            <div class="message-sender">{{ message.sender }}</div>
            <div class="message-time">{{ message.time }}</div>
          </div>
          <div class="message-content">{{ message.content }}</div>
        </div>

        <div v-if="messages.length === 0" class="no-messages">
          <el-empty description="暂无消息" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted, watch, nextTick, onUnmounted } from 'vue'
import { Close } from '@element-plus/icons-vue'
import type { MessageItem } from './types'
import { useUserStore } from '@/store/modules/user'
import { getAccessMessageApi } from '@/api/message'
import type { MessageInfo } from '@/api/message/types'

const userStore = useUserStore()

// 判断是否为管理员
const isAdminUser = computed(() => {
  const roles = userStore.getRoles || []
  console.log('FloatingMessage - 当前用户角色:', roles, '是否为管理员:', roles.includes('admin'))
  return roles.includes('admin')
})

// 窗口可见性
const isWindowVisible = ref(false)

// 消息列表引用
const messageListRef = ref<HTMLElement>()

// 自动滚动控制
let scrollAnimationId: number | null = null
let isUserScrolling = false
let userScrollTimeout: number | null = null

// 消息数据
const messages = ref<MessageItem[]>([])
const loading = ref(false)

// 格式化时间
const formatTime = (timestamp: string | number) => {
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// 获取发送者名称
const getSenderName = (msg: MessageInfo) => {
  if (msg.sender_type === 9) return '系统通知'
  if (msg.sender_type === 2) return msg.sender_user_name || '企业用户'
  return msg.sender_user_name || '用户'
}

// 获取消息列表
const fetchMessages = async () => {
  try {
    loading.value = true
    console.log('开始获取消息列表...')
    
    const res = await getAccessMessageApi({
      page: 1,
      count: 50, // 获取最近50条消息
      status: 0, // 0: 未读
      id: 0,
      user_id: 0
    })

    if (res && res.data && res.data.list) {
      console.log('获取到消息:', res.data.list.length, '条')
      
      // 转换数据格式
      messages.value = res.data.list.map((item: MessageInfo) => ({
        id: item.id,
        sender: item.user_name,
        content: item.message,
        time: item.created_at // 使用ID作为时间戳，或者后端提供时间字段
      }))
      
      console.log('消息列表已更新:', messages.value.length, '条')
    } else {
      console.log('未获取到消息数据')
      messages.value = []
    }
  } catch (error) {
    console.error('获取消息失败:', error)
    messages.value = []
  } finally {
    loading.value = false
  }
}

// 判断是否需要自动滚动（消息数量超过5条）
const shouldAutoScroll = computed(() => messages.value.length > 5)

// 停止自动滚动
const stopAutoScroll = () => {
  if (scrollAnimationId !== null) {
    cancelAnimationFrame(scrollAnimationId)
    scrollAnimationId = null
    console.log('自动滚动已停止')
  }
}

// 自动滚动函数
const startAutoScroll = () => {
  console.log('尝试启动自动滚动...')

  if (!messageListRef.value) {
    console.log('消息列表元素不存在')
    return
  }

  if (!shouldAutoScroll.value) {
    console.log('消息数量不足，无需滚动')
    return
  }

  if (!isWindowVisible.value) {
    console.log('窗口未显示')
    return
  }

  stopAutoScroll()

  const element = messageListRef.value

  // 等待DOM渲染完成
  setTimeout(() => {
    if (!element) return

    const scrollHeight = element.scrollHeight
    const clientHeight = element.clientHeight

    console.log(
      '滚动信息 - 内容高度:',
      scrollHeight,
      '容器高度:',
      clientHeight,
      '可滚动:',
      scrollHeight > clientHeight
    )

    // 如果内容高度大于容器高度，启动滚动
    if (scrollHeight > clientHeight) {
      let currentScrollTop = 0
      const scrollSpeed = 0.5 // 滚动速度（像素/帧）
      const maxScroll = scrollHeight - clientHeight

      const scroll = () => {
        if (!isWindowVisible.value || !messageListRef.value || isUserScrolling) {
          console.log('滚动中断 - 窗口关闭或用户操作')
          return
        }

        currentScrollTop += scrollSpeed

        // 滚动到底部后重新开始
        if (currentScrollTop >= maxScroll) {
          currentScrollTop = 0
        }

        element.scrollTop = currentScrollTop

        scrollAnimationId = requestAnimationFrame(scroll)
      }

      scrollAnimationId = requestAnimationFrame(scroll)
      console.log('✅ 自动滚动已成功启动')
    } else {
      console.log('内容未超出容器，无需滚动')
    }
  }, 200)
}

// 监听用户滚动
const handleUserScroll = () => {
  console.log('检测到用户滚动')
  isUserScrolling = true
  stopAutoScroll()

  // 清除之前的定时器
  if (userScrollTimeout !== null) {
    clearTimeout(userScrollTimeout)
  }

  // 3秒后恢复自动滚动
  userScrollTimeout = window.setTimeout(() => {
    console.log('恢复自动滚动')
    isUserScrolling = false
    if (isWindowVisible.value && shouldAutoScroll.value) {
      startAutoScroll()
    }
  }, 3000)
}

// 切换消息窗口显示/隐藏
const toggleMessageWindow = () => {
  isWindowVisible.value = !isWindowVisible.value
  console.log('🔄 窗口状态切换:', isWindowVisible.value ? '显示' : '隐藏')
}

// 监听窗口显示状态
watch(isWindowVisible, async (newVal) => {
  console.log('👀 监听到窗口状态变化:', newVal)
  if (newVal) {
    await nextTick()
    console.log('窗口已打开，准备启动滚动...')
    setTimeout(() => {
      startAutoScroll()
    }, 300)
  } else {
    stopAutoScroll()
    isUserScrolling = false
  }
})

// 监听管理员状态变化,登录后自动弹出
watch(
  isAdminUser,
  async (newVal) => {
    console.log('🔐 管理员状态变化:', newVal)
    if (newVal) {
      // 获取消息数据
      await fetchMessages()
      
      if (!isWindowVisible.value) {
        console.log('检测到管理员登录，准备弹出窗口...')
        // 延迟1秒后自动弹出
        setTimeout(() => {
          console.log('🚀 自动弹出消息窗口')
          isWindowVisible.value = true
        }, 1000)
      }
    }
  },
  { immediate: true }
)

// 组件挂载后
onMounted(async () => {
  console.log('📦 FloatingMessage组件已挂载')
  console.log('当前是否为管理员:', isAdminUser.value)

  // 添加滚动监听
  await nextTick()
  if (messageListRef.value) {
    messageListRef.value.addEventListener('wheel', handleUserScroll, { passive: true })
    messageListRef.value.addEventListener('touchmove', handleUserScroll, { passive: true })
    console.log('滚动事件监听器已添加')
  }

  // 如果已经是管理员（刷新页面的情况），获取消息并自动弹出
  if (isAdminUser.value) {
    await fetchMessages()
    
    setTimeout(() => {
      console.log('页面加载时检测到管理员，自动弹出窗口')
      isWindowVisible.value = true
    }, 1500)
  }
})

// 组件卸载时清理
onUnmounted(() => {
  console.log('组件卸载，清理资源...')
  stopAutoScroll()
  if (userScrollTimeout !== null) {
    clearTimeout(userScrollTimeout)
  }
  if (messageListRef.value) {
    messageListRef.value.removeEventListener('wheel', handleUserScroll)
    messageListRef.value.removeEventListener('touchmove', handleUserScroll)
  }
})
</script>

<style lang="less" scoped>
.floating-message-container {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9999;
  pointer-events: none;
}

.message-window {
  position: relative;
  width: 350px;
  height: 600px;
  background-color: white;
  border-radius: 8px 0 0 8px;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  transform: translateX(100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: visible;
  pointer-events: auto;

  &:not(.show) {
    pointer-events: none;
  }

  &.show {
    transform: translateX(0);
  }
}

/* 可拉出的角标 - 贴在窗口左侧边缘，随窗口一起滑动 */
.pull-corner {
  position: absolute;
  left: -30px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 80px;
  background-color: white;
  border-radius: 10px 0 0 10px;
  box-shadow: -3px 0 15px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  z-index: 10;
  pointer-events: auto;

  &:hover {
    background-color: #f5f7fa;
    width: 35px;
  }
}

.corner-line {
  width: 16px;
  height: 3px;
  background-color: #409eff;
  border-radius: 2px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  .pull-corner:hover & {
    width: 20px;
  }
}

.message-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
}

.message-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 15px;
  height: 100%;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.message-item {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.message-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.message-sender {
  font-weight: 600;
  font-size: 14px;
  max-width: 10em; /* 最多显示8个字符宽度 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.4;
}

.message-time {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  margin-left: 10px;
}

.no-messages {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
